import axios from 'axios';
import i18n from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Get locale based on current language
const getLocale = () => {
  return i18n.language === 'ar' ? 'ar-SA' : 'en-SA';
};

export const formatDate = (isoString) => {
  if (!isoString) return '\u2014';
  return new Date(isoString).toLocaleString(getLocale(), {
    timeZone: 'Asia/Riyadh',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatShortDate = (isoString) => {
  if (!isoString) return '\u2014';
  return new Date(isoString).toLocaleDateString(getLocale(), {
    timeZone: 'Asia/Riyadh',
    month: 'short',
    day: 'numeric',
  });
};

export const formatCurrency = (amount, options = {}) => {
  const locale = getLocale();
  const value = parseFloat(amount) || 0;
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
};

/**
 * Get error message from API error response.
 * Super Admin sees detailed error; others see friendly message.
 * @param {Error} err - Axios error object
 * @param {string} fallback - Fallback message for non-super_admin users
 * @param {object} user - Current user object (to check role)
 * @returns {string} Error message to display
 */
export const getErrorMessage = (err, fallback = 'An error occurred', user = null) => {
  const detail = err.response?.data?.detail;
  const message = err.response?.data?.message;
  const code = err.response?.data?.code;
  const status = err.response?.status;
  
  // If user is super_admin, show detailed error
  if (user?.role === 'super_admin') {
    const parts = [];
    if (status) parts.push(`[${status}]`);
    if (code) parts.push(code);
    if (detail) parts.push(detail);
    else if (message) parts.push(message);
    return parts.length > 0 ? parts.join(' ') : fallback;
  }
  
  // For other users, show detail if available, otherwise fallback
  return detail || message || fallback;
};
